import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Brain, Target, TrendingUp, AlertTriangle, CheckCircle,
  ChevronRight, ChevronLeft, ArrowLeft, Loader2,
  Award, Clock, Home, FileText, Zap, BarChart3,
  BookOpen, PenLine, Shield, Flame, Calendar, Star
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PracticeMock = ({ onBack, studentClass = "6" }) => {
  const [view, setView] = useState('home'); // home, generating, test, results
  const [subjects] = useState(['Mathematics', 'Science', 'English']);
  const [selectedSubject, setSelectedSubject] = useState('Mathematics');
  const [selectedExamType, setSelectedExamType] = useState('Quarterly');

  // Confidence & Weak Areas
  const [confidence, setConfidence] = useState(null);
  const [weakAreas, setWeakAreas] = useState(null);
  const [history, setHistory] = useState([]);
  const [examCountdown, setExamCountdown] = useState([]);

  // Test state
  const [mockData, setMockData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Timer
  useEffect(() => {
    if (view === 'test' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timer); handleSubmit(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [view, timeLeft]);

  const loadDashboardData = async () => {
    try {
      const [confRes, weakRes, histRes, countRes] = await Promise.all([
        axios.get(`${API}/practice-mock/confidence`),
        axios.get(`${API}/practice-mock/weak-areas`),
        axios.get(`${API}/practice-mock/history`),
        axios.get(`${API}/student/exam-countdown`)
      ]);
      setConfidence(confRes.data);
      setWeakAreas(weakRes.data);
      setHistory(histRes.data.history || []);
      setExamCountdown(countRes.data.exams || []);
    } catch (e) {
      console.error(e);
    }
  };

  const generateMock = async () => {
    setLoading(true);
    setView('generating');
    try {
      const res = await axios.post(`${API}/practice-mock/generate`, {
        subject: selectedSubject,
        exam_type: selectedExamType
      });
      setMockData(res.data);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setTimeLeft(res.data.time_minutes * 60);
      setView('test');
      toast.success('Mock paper generated! Good luck!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate mock');
      setView('home');
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const handleSubmit = async () => {
    if (!mockData) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/practice-mock/${mockData.mock_id}/submit`, { answers });
      setTestResults(res.data);
      setView('results');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}`;

  const getStatusColor = (status) => {
    if (status === 'green') return 'text-green-600 bg-green-50 border-green-200';
    if (status === 'orange') return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getStatusBg = (status) => {
    if (status === 'green') return 'from-green-500 to-green-600';
    if (status === 'orange') return 'from-orange-500 to-orange-600';
    return 'from-red-500 to-red-600';
  };

  // ===== RESULTS VIEW =====
  if (view === 'results' && testResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-50">
        <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white px-6 py-4 shadow-lg">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <button onClick={() => { setView('home'); setTestResults(null); setMockData(null); }} className="flex items-center gap-2 hover:text-teal-200" data-testid="back-from-results">
              <ArrowLeft size={20} /> <span className="font-medium">Back to Practice</span>
            </button>
            <Badge className="bg-white/20 text-white border-white/30">{mockData?.exam_type} Mock</Badge>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-6 space-y-6">
          {/* Score Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-teal-100">
            <div className={`p-8 text-center text-white bg-gradient-to-r ${
              testResults.score_percent >= 75 ? 'from-green-500 to-teal-500' :
              testResults.score_percent >= 50 ? 'from-yellow-500 to-orange-500' :
              'from-red-500 to-pink-500'
            }`}>
              <div className="w-28 h-28 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4 shadow-lg">
                <span className="text-5xl font-bold" data-testid="mock-score">{testResults.score_percent}%</span>
              </div>
              <h1 className="text-2xl font-bold">
                {testResults.score_percent >= 75 ? 'Excellent!' : testResults.score_percent >= 50 ? 'Good Effort!' : 'Keep Practicing!'}
              </h1>
              <p className="opacity-80 mt-1">{testResults.earned_marks} / {testResults.total_marks} marks</p>
            </div>

            {/* Chapter Breakdown */}
            <div className="p-6">
              <h3 className="font-bold text-gray-800 mb-3">Chapter-wise Performance</h3>
              <div className="space-y-2">
                {testResults.chapter_breakdown?.map((ch, i) => (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${getStatusColor(ch.status)}`}>
                    <span className="font-medium text-sm">{ch.chapter}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{ch.score_percent}%</span>
                      <span className="text-xs">({ch.earned}/{ch.total})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Weak Areas */}
          {testResults.weak_areas?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-orange-100">
              <h3 className="font-bold text-orange-800 mb-3 flex items-center gap-2"><AlertTriangle size={18} /> Areas to Improve</h3>
              <div className="space-y-3">
                {testResults.weak_areas.map((area, i) => (
                  <div key={i} className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                    <div className="flex items-start gap-2 mb-2">
                      <p className="text-sm font-semibold text-gray-800 flex-1">Q{i + 1}. {area.question}</p>
                      <Badge className="bg-orange-100 text-orange-600 text-xs shrink-0">{area.type === 'mcq' ? 'MCQ' : area.type === 'short_answer' ? '2M' : '4M'}</Badge>
                    </div>

                    {/* MCQ: show all options highlighting student's pick + correct */}
                    {area.type === 'mcq' && area.options && (
                      <div className="space-y-1 mb-2">
                        {Object.entries(area.options).map(([letter, text]) => {
                          const isCorrect = letter === area.correct_answer;
                          const isStudent = letter === area.student_answer;
                          const cls = isCorrect
                            ? 'bg-green-50 border-green-300 text-green-900'
                            : isStudent
                              ? 'bg-red-50 border-red-300 text-red-900'
                              : 'bg-white border-gray-200 text-gray-700';
                          return (
                            <div key={letter} className={`flex items-start gap-2 p-2 rounded border text-xs ${cls}`}>
                              <span className="font-semibold">{letter}.</span>
                              <span className="flex-1">{text}</span>
                              {isCorrect && <span className="text-green-700 font-medium shrink-0">✓ Correct</span>}
                              {isStudent && !isCorrect && <span className="text-red-700 font-medium shrink-0">✗ Your answer</span>}
                            </div>
                          );
                        })}
                        {!area.student_answer && (
                          <p className="text-xs text-amber-700 italic">You did not answer this question.</p>
                        )}
                      </div>
                    )}

                    {/* Written: show student's text + model answer */}
                    {area.type !== 'mcq' && (
                      <div className="space-y-2 mb-2">
                        <div className="bg-red-50 border border-red-200 rounded p-2">
                          <p className="text-xs font-semibold text-red-700 mb-1">Your answer{typeof area.marks_earned === 'number' && ` (${area.marks_earned}/${area.max_marks ?? '-'} marks)`}:</p>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{area.student_answer || <em className="text-gray-400">Not answered</em>}</p>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <p className="text-xs font-semibold text-green-700 mb-1">Model answer:</p>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{area.correct_answer}</p>
                        </div>
                      </div>
                    )}

                    {/* Fallback for MCQ without options dict */}
                    {area.type === 'mcq' && !area.options && (
                      <p className="text-xs text-green-700"><CheckCircle size={12} className="inline mr-1" />Correct: {area.correct_answer}</p>
                    )}

                    {/* Explanation */}
                    {area.explanation && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                        <p className="text-xs font-semibold text-blue-700 mb-0.5">Why:</p>
                        <p className="text-xs text-blue-900">{area.explanation}</p>
                      </div>
                    )}

                    {/* AI feedback (written) */}
                    {area.feedback && (
                      <p className="text-xs text-teal-700 mt-2 bg-teal-50 p-2 rounded border border-teal-200">
                        <strong>AI Feedback:</strong> {area.feedback}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={() => { setView('home'); setTestResults(null); setMockData(null); }} variant="outline" className="flex-1 border-teal-200" data-testid="back-home-btn">
              <Home className="mr-2" size={16} /> Dashboard
            </Button>
            <Button onClick={() => { setTestResults(null); setMockData(null); generateMock(); }} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="retry-mock-btn">
              <Zap className="mr-2" size={16} /> Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ===== TEST VIEW =====
  if (view === 'test' && mockData) {
    const q = mockData.questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / mockData.total_questions) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-50">
        <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white px-4 py-4 sticky top-0 z-10 shadow-lg">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Badge className="bg-white/20 text-white border-white/30">{mockData.exam_type}</Badge>
                <Badge className="bg-white/20 text-white border-white/30">{mockData.subject}</Badge>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold bg-white/20 px-3 py-1 rounded-full">{currentQuestionIndex + 1} / {mockData.total_questions}</span>
                <span className={`text-sm font-bold px-3 py-1 rounded-full ${timeLeft < 60 ? 'bg-red-500' : 'bg-white/20'}`}>
                  <Clock size={14} className="inline mr-1" />{formatTime(timeLeft)}
                </span>
              </div>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-4">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-4 border border-teal-100">
            <div className="flex gap-2 mb-4">
              {q.type === 'mcq' && <Badge className="bg-gray-100 text-gray-600">MCQ - 1 Mark</Badge>}
              {q.type === 'short_answer' && <Badge className="bg-orange-100 text-orange-700"><PenLine size={12} className="mr-1" />Short Answer - 2 Marks</Badge>}
              {q.type === 'descriptive' && <Badge className="bg-purple-100 text-purple-700"><PenLine size={12} className="mr-1" />Descriptive - 4 Marks</Badge>}
              {q.chapter && <Badge className="bg-teal-50 text-teal-600 text-xs">{q.chapter}</Badge>}
            </div>

            <h2 className="text-lg font-semibold text-gray-800 mb-6" data-testid="mock-question-text">{q.question}</h2>

            {q.type === 'mcq' && q.options && (
              <div className="space-y-3">
                {Object.entries(q.options).map(([key, value]) => (
                  <button key={key} onClick={() => selectAnswer(q.id, key)} data-testid={`mock-option-${key}`}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      answers[q.id] === key ? 'border-teal-500 bg-teal-50 shadow-md' : 'border-gray-200 hover:border-teal-300'
                    }`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        answers[q.id] === key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>{key}</span>
                      <span className="font-medium">{value}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {q.type === 'short_answer' && (
              <div>
                <Textarea value={answers[q.id] || ''} onChange={(e) => selectAnswer(q.id, e.target.value)}
                  placeholder="Write your answer (1-2 sentences)..."
                  className="min-h-[80px] border-2 border-gray-200 focus:border-teal-500 rounded-xl p-4"
                  data-testid="mock-short-input" />
                <p className="text-xs text-gray-400 mt-1">Short answer for 2 marks</p>
              </div>
            )}

            {q.type === 'descriptive' && (
              <div>
                <Textarea value={answers[q.id] || ''} onChange={(e) => selectAnswer(q.id, e.target.value)}
                  placeholder="Write a detailed answer (3-5 sentences)..."
                  className="min-h-[140px] border-2 border-gray-200 focus:border-teal-500 rounded-xl p-4"
                  data-testid="mock-desc-input" />
                <p className="text-xs text-gray-400 mt-1">Detailed answer for 4 marks</p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            {currentQuestionIndex > 0 && (
              <Button variant="outline" onClick={() => setCurrentQuestionIndex(p => p - 1)} className="flex-1 border-teal-200 text-teal-700" data-testid="mock-prev-btn">
                <ChevronLeft size={18} className="mr-1" /> Previous
              </Button>
            )}
            {currentQuestionIndex < mockData.total_questions - 1 ? (
              <Button onClick={() => setCurrentQuestionIndex(p => p + 1)} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="mock-next-btn">
                Next <ChevronRight size={18} className="ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="mock-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="mr-2" size={18} />}
                Submit Mock
              </Button>
            )}
          </div>

          <div className="mt-4 bg-white rounded-2xl shadow-lg p-4 border border-teal-100">
            <p className="text-sm text-teal-600 font-medium mb-3">Questions:</p>
            <div className="flex flex-wrap gap-2">
              {mockData.questions.map((mq, idx) => (
                <button key={mq.id} onClick={() => setCurrentQuestionIndex(idx)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                    currentQuestionIndex === idx ? 'bg-teal-600 text-white shadow-md' :
                    answers[mq.id] ? 'bg-teal-100 text-teal-700 border border-teal-200' :
                    'bg-gray-100 text-gray-600 hover:bg-teal-50'
                  }`}>{idx + 1}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== GENERATING VIEW =====
  if (view === 'generating') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-xl p-10 border border-teal-100">
          <Brain className="w-16 h-16 text-teal-600 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">AI is creating your {selectedExamType} Mock Paper</h2>
          <p className="text-gray-500 mb-4">{selectedSubject} - Class {studentClass}</p>
          <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
          <p className="text-sm text-teal-600 mt-4">Generating questions from syllabus...</p>
        </div>
      </div>
    );
  }

  // ===== HOME VIEW =====
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-50">
      <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white px-6 py-4 shadow-lg">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-teal-600 rounded-lg" data-testid="back-to-dashboard">
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Quick Practice</h1>
              <p className="text-teal-200 text-sm">10 questions on any chapter — anytime, unlimited</p>
            </div>
          </div>
          <Badge className="bg-white/20 text-white border-white/30">Class {studentClass}</Badge>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">

        {/* Exam Countdown */}
        {examCountdown.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {examCountdown.map((exam, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md p-4 border-l-4 border-teal-500 flex items-center gap-4">
                <div className="p-3 bg-teal-50 rounded-xl">
                  <Calendar className="text-teal-600" size={24} />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-800">{exam.label}</p>
                  <p className="text-sm text-gray-500">{exam.days_left} days left</p>
                  {exam.confidence > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <Shield size={12} className={exam.confidence >= 75 ? 'text-green-500' : exam.confidence >= 50 ? 'text-orange-500' : 'text-red-500'} />
                      <span className="text-xs font-medium">{exam.confidence}% ready</span>
                    </div>
                  )}
                </div>
                <span className="text-2xl font-bold text-teal-600">{exam.days_left}d</span>
              </div>
            ))}
          </div>
        )}

        {/* Generate New Mock */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-teal-100">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-5 text-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl"><Zap size={28} /></div>
              <div>
                <h2 className="text-lg font-bold">Generate Practice Mock</h2>
                <p className="text-teal-100 text-sm">AI creates fresh questions every time - just like the real exam!</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Subject</label>
                <div className="flex gap-1">
                  {subjects.map(s => (
                    <button key={s} onClick={() => setSelectedSubject(s)} data-testid={`mock-subject-${s.toLowerCase()}`}
                      className={`flex-1 py-2 px-2 rounded-lg text-sm font-medium transition-all ${
                        selectedSubject === s ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-teal-50'
                      }`}>{s}</button>
                  ))}
                </div>
              </div>
              {/* Exam Type */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Exam Type</label>
                <div className="flex gap-1">
                  {['Quarterly', 'Half-yearly', 'Finals'].map(t => (
                    <button key={t} onClick={() => setSelectedExamType(t)} data-testid={`mock-type-${t.toLowerCase()}`}
                      className={`flex-1 py-2 px-2 rounded-lg text-sm font-medium transition-all ${
                        selectedExamType === t ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-teal-50'
                      }`}>{t}</button>
                  ))}
                </div>
              </div>
              {/* Start */}
              <div className="flex items-end">
                <Button onClick={generateMock} disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 h-10" data-testid="generate-mock-btn">
                  <Brain className="mr-2" size={18} /> Generate Mock Paper
                </Button>
              </div>
            </div>
            <div className="flex gap-4 text-xs text-gray-500">
              <span><FileText size={12} className="inline mr-1" />MCQ + Short Answer + Descriptive</span>
              <span><Clock size={12} className="inline mr-1" />Timed like real exam</span>
              <span><Zap size={12} className="inline mr-1" />Fresh questions every time</span>
            </div>
          </div>
        </div>

        {/* Confidence Meter */}
        {confidence?.has_data && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-teal-100">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-800 flex items-center gap-2"><Shield size={18} className="text-teal-600" /> Exam Readiness</h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(confidence.subjects).map(([subj, exams]) => (
                  <div key={subj} className="border rounded-xl overflow-hidden">
                    <div className="bg-teal-50 px-4 py-2 font-bold text-teal-800 text-sm">{subj}</div>
                    <div className="p-3 space-y-3">
                      {exams.map((exam, i) => (
                        <div key={i}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-700">{exam.exam_type}</span>
                            <span className={`text-sm font-bold ${exam.status === 'green' ? 'text-green-600' : exam.status === 'orange' ? 'text-orange-600' : 'text-red-600'}`}>
                              {exam.readiness_percent}%
                            </span>
                          </div>
                          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full bg-gradient-to-r ${getStatusBg(exam.status)}`}
                              style={{ width: `${exam.readiness_percent}%` }} />
                          </div>
                          <div className="flex justify-between text-xs text-gray-400 mt-1">
                            <span>{exam.attempts} attempts</span>
                            <span>{exam.trend === 'improving' ? 'Improving' : 'Stable'}</span>
                          </div>
                          {/* Chapter readiness */}
                          {exam.chapter_readiness?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {exam.chapter_readiness.map((ch, ci) => (
                                <div key={ci} className="flex items-center gap-2 text-xs">
                                  <span className={`w-2 h-2 rounded-full ${ch.status === 'green' ? 'bg-green-500' : ch.status === 'orange' ? 'bg-orange-500' : 'bg-red-500'}`} />
                                  <span className="flex-1 text-gray-600 truncate">{ch.chapter}</span>
                                  <span className="font-medium">{ch.avg_score}%</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Weak Areas */}
        {weakAreas?.has_data && weakAreas.weak_chapters?.length > 0 && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-red-100">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-800 flex items-center gap-2"><Flame size={18} className="text-red-500" /> Focus Areas</h2>
              <Badge className="bg-red-50 text-red-600 border-red-200">{weakAreas.weak_chapters.length} chapters need attention</Badge>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {weakAreas.weak_chapters.map((ch, i) => (
                <div key={i} className={`rounded-lg p-4 border ${getStatusColor(ch.status)}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{ch.chapter}</span>
                    <Badge className={`text-xs ${ch.status === 'red' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>
                      {ch.avg_score}%
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500">{ch.subject} - {ch.attempts} attempts</p>
                  <p className="text-xs mt-1 font-medium">{ch.suggestion}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-teal-100">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-800 flex items-center gap-2"><BarChart3 size={18} className="text-teal-600" /> Recent Practice</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {history.slice(0, 10).map((h, i) => (
                <div key={i} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm bg-gradient-to-r ${getStatusBg(h.score_percent >= 75 ? 'green' : h.score_percent >= 50 ? 'orange' : 'red')}`}>
                      {h.score_percent}%
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{h.subject} - {h.exam_type}</p>
                      <p className="text-xs text-gray-400">{h.earned_marks}/{h.total_marks} marks - {new Date(h.completed_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {h.chapter_breakdown?.map((ch, ci) => (
                      <span key={ci} className={`w-3 h-3 rounded-full ${ch.status === 'green' ? 'bg-green-500' : ch.status === 'orange' ? 'bg-orange-500' : 'bg-red-500'}`}
                        title={`${ch.chapter}: ${ch.score_percent}%`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!confidence?.has_data && (
          <div className="bg-white rounded-xl shadow-md p-12 text-center border border-teal-100">
            <Brain className="w-16 h-16 text-teal-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-700 mb-2">Start Your First Practice Mock!</h3>
            <p className="text-gray-500 mb-4">AI will generate a fresh exam paper every time. The more you practice, the more confident you'll be!</p>
            <p className="text-sm text-teal-600">Select subject and exam type above, then click "Generate Mock Paper"</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PracticeMock;
